import React, { useMemo, useState } from 'react';
import { ChevronDown, LucideChevronRight, LucidePlus } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../UI/dropdown-menu';
import {
  buildRoleAssignmentManageEntries,
  countRoleAssignmentManageEntriesForRole,
  type RoleAssignmentOption,
} from '../../helper/roleAssignments';
import { Checkbox } from '../ui/checkbox';

interface RoleAssignmentDropdownProps {
  value: string;
  options: RoleAssignmentOption[];
  open?: boolean;
  onOpenChange?(nextOpen: boolean): void;
  disabled?: boolean;
  isAccountAdmin?: boolean;
  onChange: (nextValue: string) => void;
  dataQa?: string;
  className?: string;
  manageAssignments?(): void;
  handleUpdateTeamMember?(val: any, member: any, role: any): void;
  teamMembers?: any[];
  assignees?: any[];
  handleUpdateAssignee?(val: any, assignee: any, role: any): void;
  role: any;
  handleClearValue?(): void;
}

const RoleAssignmentDropdown: React.FC<RoleAssignmentDropdownProps> = ({
  value,
  options,
  open: controlledOpen,
  onOpenChange,
  disabled = false,
  isAccountAdmin = false,
  onChange,
  dataQa,
  className,
  manageAssignments,
  handleUpdateTeamMember,
  teamMembers = [],
  assignees = [],
  handleUpdateAssignee,
  role,
  handleClearValue,
}) => {
  const [internalOpen, setInternalOpen] = useState<boolean>(false);
  const [openAssignments, setOpenAssignments] = useState<boolean>(false);
  const open = controlledOpen ?? internalOpen;
  const selectedOption = options.find((option) => option.key === value);
  const triggerLabel = selectedOption?.shortLabel || 'Select';

  const setOpenState = (nextOpen: boolean) => {
    if (typeof controlledOpen === 'undefined') {
      setInternalOpen(nextOpen);
    }
    if (onOpenChange) {
      onOpenChange(nextOpen);
    }
  };

  const handleOpenPopover = (val: any) => {
    if (!val) {
      setOpenAssignments(false);
    }
    setOpenState(Boolean(val));
  };

  const handleOpenAssignments = () => {
    setOpenAssignments((prev) => !prev);
  };

  const manageEntries = useMemo(
    () => buildRoleAssignmentManageEntries(teamMembers, assignees),
    [teamMembers, assignees]
  );
  const roleUsage = countRoleAssignmentManageEntriesForRole(manageEntries, role?.id);
  return (
    <DropdownMenu open={open} onOpenChange={handleOpenPopover} modal={false}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <button
          type="button"
          data-qa={dataQa}
          className={cn(
            'inline-flex h-9 w-full items-center justify-start gap-1 px-0 text-left text-sm text-[#0f172a] bg-transparent border-0 shadow-none hover:bg-[#f8fafc] focus-visible:outline-none focus-visible:ring-0 disabled:pointer-events-none disabled:opacity-60',
            className
          )}
        >
          <span className={`truncate ${triggerLabel === 'Select' ? 'text-muted-foreground' : ''}`}>
            {triggerLabel}
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={4}
        className="min-w-[240px] max-w-[308.36px] max-h-[400px] overflow-y-auto rounded-[8px] border border-[#d5dde5] bg-white p-1 shadow-[0_10px_28px_rgba(15,23,42,0.15)] z-[999]"
      >
        {options.length > 0 ? (
          <>
            {triggerLabel !== 'Select' && (
              <>
                <div
                  onClick={() => {
                    if (handleClearValue) {
                      handleClearValue();
                      handleOpenPopover(false)
                    }
                  }}
                  className="relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-muted-foreground italic"
                >
                  Clear selection
                </div>
                <div className="-mx-1 my-1 h-px bg-muted" />
              </>
            )}
            {options.map((option) => (
              <DropdownMenuItem
                key={option.key}
                className="relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer text-gray-500"
                onSelect={() => onChange(option.key)}
              >
                {option.label}
              </DropdownMenuItem>
            ))}
          </>
        ) : (
          <div className="px-3 py-2 text-[12px] leading-5 text-[#64748b]">
            {isAccountAdmin ? (
              <div className="text-sm text-muted-foreground">
                No team members or assignees assigned to this role
              </div>
            ) : (
              <>
                No team members or assignees are assigned to this role yet. Ask your admin to
                configure assignments. See your admin{' '}
                <Link to="/team_settings" className="font-medium text-[#2563eb] hover:underline">
                  here
                </Link>
                .
              </>
            )}
          </div>
        )}

        {isAccountAdmin && (
          <>
            <div className="w-full bg-input h-[1px] shrink-0" />

            <div className="px-2 py-1">
              <div>
                <button
                  onClick={handleOpenAssignments}
                  type="button"
                  className="w-full flex items-center justify-between text-xs text-muted-foreground hover:text-foreground py-1"
                >
                  <span>Manage assignment</span>

                  <span className="flex items-center gap-1">
                    {`(${roleUsage})`}
                    <LucideChevronRight
                      data-open={openAssignments}
                      className="lucide lucide-chevron-right h-3 w-3 data-[open=true]:rotate-90"
                    />
                  </span>
                </button>

                {openAssignments && (
                  <div>
                    <div className="pt-2 pb-1 space-y-1 border-t border-border mt-1">
                      {manageEntries.map((entry) => (
                        <div key={entry.key} className="flex items-center gap-2">
                          <Checkbox
                            checked={entry.roleIds.includes(role?.id)}
                            data-qa={`assignment_column_${role?.name}_checkbox_trigger`}
                            onPointerDown={(e) => {
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                            }}
                            onCheckedChange={(val) => {
                              if (entry.type === 'team_member') {
                                handleUpdateTeamMember?.(val, entry.teamMember, role);
                              } else {
                                handleUpdateAssignee?.(val, entry.assignee, role);
                              }
                              setOpenState(true);
                              setOpenAssignments(true);
                            }}
                          />
                          <span className="text-sm">{entry.name}</span>
                          {entry.label ? (
                            <span className="text-xs text-muted-foreground">@{entry.label}</span>
                          ) : null}
                        </div>
                      ))}

                      <button
                        data-qa={`add_team_member_to_role_${role?.name}`}
                        onClick={() => {
                          if (manageAssignments) {
                            manageAssignments();
                            handleOpenPopover(false);
                          }
                        }}
                        type="button"
                        className="text-xs text-primary hover:underline mt-2 flex items-center gap-1"
                      >
                        <LucidePlus className="lucide lucide-plus h-3 w-3" />
                        Add Team Member
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RoleAssignmentDropdown;
